import { Layout, Menu } from "antd";
import type { MenuProps } from "antd";
import { useNavigate, useLocation } from "react-router-dom";

const { Sider } = Layout;

// Overriding Ant Menu Styles for Custom Light Theme
const menuStyles = `
  .ant-menu-item {
    color: #6b7280 !important; /* slate-500 */
    border-radius: 6px !important;
    margin-bottom: 4px !important;
  }
  .ant-menu-item:hover {
    color: #10b981 !important; /* emerald-500 */
    background-color: #ecfdf5 !important; /* emerald-50 */
  }
  .ant-menu-item-selected {
    color: #10b981 !important;
    background-color: #ecfdf5 !important;
    font-weight: 600 !important;
    border-right: 3px solid #10b981 !important;
  }
  .ant-menu-submenu-title:hover {
    color: #10b981 !important;
  }
`;

export interface MenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  permissions?: string[];
}

interface SidebarProps {
  items: MenuItem[];
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
  className?: string;
}

export function Sidebar({
  items,
  collapsed = false,
  onCollapse,
  className = "",
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Convertir items a formato de Ant Design
  const menuItems: MenuProps["items"] = items.map((item) => ({
    key: item.key,
    icon: item.icon,
    label: item.label,
    children: item.children?.map((child) => ({
      key: child.key,
      icon: child.icon,
      label: child.label,
    })),
  }));

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    const findPath = (items: MenuItem[], key: string): string | undefined => {
      for (const item of items) {
        if (item.key === key) return item.path;
        if (item.children) {
          const path = findPath(item.children, key);
          if (path) return path;
        }
      }
      return undefined;
    };

    const path = findPath(items, key);
    if (path) {
      navigate(path);
    }
  };

  // Obtener la key activa basada en la ruta actual
  const getSelectedKey = (): string => {
    const findKey = (items: MenuItem[]): string | undefined => {
      for (const item of items) {
        if (item.path === location.pathname) return item.key;
        if (item.children) {
          const key = findKey(item.children);
          if (key) return key;
        }
      }
      return undefined;
    };
    return findKey(items) || "";
  };

  return (
    <>
      <style>{menuStyles}</style>
      <Sider
        collapsed={collapsed}
        onCollapse={onCollapse}
        width={260}
        collapsedWidth={80}
        className={`${className} border-r border-gray-200`}
        style={{
          minHeight: "100%",
          backgroundColor: "#ffffff",
          position: "sticky",
          top: 0,
          height: "100vh",
          overflow: "auto",
        }}
        trigger={null}
      >
        <div className="h-16 flex items-center gap-3 px-6 mb-2 border-b border-gray-100">
          <div className="w-8 h-8 rounded bg-emerald-500 flex items-center justify-center text-white font-bold text-sm">
            AP
          </div>
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-lg font-bold text-slate-800 tracking-tight leading-none">
                ADP
              </span>
              <span className="text-xs text-slate-400 font-medium tracking-widest">
                FUTBOL
              </span>
            </div>
          )}
        </div>

        <div className="px-3 py-2">
          <span
            className={`text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2 block ${collapsed ? "text-center" : ""}`}
          >
            {collapsed ? "..." : "Menu"}
          </span>
          <Menu
            mode="inline"
            selectedKeys={[getSelectedKey()]}
            items={menuItems}
            onClick={handleMenuClick}
            className="bg-transparent border-none"
            style={{
              background: "transparent",
              border: "none",
              color: "#6b7280", // slate-500
            }}
            // Customizing Ant Design Menu items via global CSS or style overrides would be ideal,
            // but relying on our .nav-item utility class logic in parent items wrapper if possible.
            // Since we use Ant Menu, we rely on updated theme vars or Ant styles.
            // For now, let's trust the Ant Design integration or the custom CSS updates.
          />
        </div>
      </Sider>
    </>
  );
}
